import { AxiosError } from "axios";

/**
 * Error codes of the manage routes (C-14; backend `errors.go` 1237-1244) and
 * the switch-off test shared by every manage page.
 */

/** glen's NoRoute: the route isn't registered (`user_roles_enabled` off). */
export const MANAGE_SWITCHED_OFF_ERROR_CODE = 1035;

export const MANAGE_ERROR_CODES = {
  SELF_CHANGE: 1237,
  LAST_ADMIN: 1238,
  IMPERSONATING: 1239,
  BAD_GRANT_SHAPE: 1240,
  MEMBER_NOT_ON_ROSTER: 1241,
  POD_LEAD_CONFLICT: 1242,
  UNVERIFIED_EMAIL: 1243,
  BAD_POD_NAME: 1244,
} as const;

const MANAGE_ERROR_MESSAGES: Record<number, string> = {
  [MANAGE_ERROR_CODES.SELF_CHANGE]: "You can't change your own role.",
  [MANAGE_ERROR_CODES.LAST_ADMIN]:
    "This is the last Admin. Grant Admin to someone else first.",
  [MANAGE_ERROR_CODES.IMPERSONATING]:
    "Changes are turned off while you're impersonating someone.",
  [MANAGE_ERROR_CODES.BAD_GRANT_SHAPE]:
    "That role needs a workspace (LGA) or a pod (Pod Lead).",
  [MANAGE_ERROR_CODES.MEMBER_NOT_ON_ROSTER]:
    "That caller isn't on this workspace's roster, or is already in a pod.",
  [MANAGE_ERROR_CODES.POD_LEAD_CONFLICT]:
    "The pod already has a lead, or the lead isn't a member of the pod.",
  [MANAGE_ERROR_CODES.BAD_POD_NAME]:
    "Use a name of 1 to 80 characters that no other pod in this workspace has.",
  [MANAGE_ERROR_CODES.UNVERIFIED_EMAIL]:
    "Verify your email address before managing roles.",
};

export const GENERIC_MANAGE_FAILURE = "Something went wrong. Please try again.";

const readError = (error: unknown) => {
  const response = (error as AxiosError<{ error_code?: number }>)?.response;

  return {
    status: response?.status,
    code:
      typeof response?.data?.error_code === "number"
        ? response.data.error_code
        : undefined,
  };
};

/**
 * The roles switch is off: 404 with error_code 1035. A 404 without that code
 * (a proxy, a network edge) is some other failure, not "off".
 */
export const isManageSwitchedOff = (error: unknown): boolean => {
  const { status, code } = readError(error);

  return status === 404 && code === MANAGE_SWITCHED_OFF_ERROR_CODE;
};

/**
 * The pod name was refused (C14-B2-D25): blank, over 80 characters, or taken
 * by another live pod of the workspace, case-insensitively. Shown on the field.
 */
export const isBadPodName = (error: unknown): boolean =>
  readError(error).code === MANAGE_ERROR_CODES.BAD_POD_NAME;

/** A sentence for a failed manage write. */
export const describeManageFailure = (error: unknown): string => {
  const { code } = readError(error);

  return (code && MANAGE_ERROR_MESSAGES[code]) || GENERIC_MANAGE_FAILURE;
};

/** A 401: the route refuses this caller, or (B-4) predates the request. */
export const isUnauthorized = (error: unknown): boolean =>
  readError(error).status === 401;
